export function parseSaleInstant(value) {
  const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|[+-]\d{2}:\d{2})$/.exec(value || '');
  if (!parts) return NaN;
  const [, year, month, day, hour, minute, second, zone] = parts;
  const days = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
  if (Number(month) < 1 || Number(month) > 12 || Number(day) < 1 || Number(day) > days ||
      Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59) return NaN;
  if (zone !== 'Z') {
    const [hours, minutes] = zone.slice(1).split(':').map(Number);
    if (hours > 14 || minutes > 59 || (hours === 14 && minutes !== 0)) return NaN;
  }
  return Date.parse(value);
}

export function saleState({ enabled, start, end, now }) {
  if (!enabled) return 'disabled';
  if (![start, end, now].every(Number.isFinite) || start >= end) return 'invalid';
  if (now < start) return 'upcoming';
  if (now >= end) return 'ended';
  return 'active';
}

export function createHalloweenMotion(root, signal) {
  if (!root.querySelector('.lv3hw-scene')) return null;
  let S = 1, mode = 'wide', Lo = { W: 1920, H: 1080 };
  const onLayout = [];
  const beam = root.querySelector('.lv3hw-beam');
  let layoutWidth = 0;
  const resize = () => {
    const width = Math.max(1, root.clientWidth);
    if (width === layoutWidth) return;
    layoutWidth = width;
    mode = width < 760 ? 'tall' : 'wide';
    Lo = mode === 'tall' ? { W: 1080, H: 1830 } : { W: 1920, H: 1080 };
    S = width / Lo.W;
    root.style.setProperty('--lv3hw-beam-radius', `${340*S}px`);
    onLayout.forEach(callback => callback());
  };
  resize();
  const FRAG = `precision mediump float;uniform vec2 r;uniform float t;uniform vec3 col;uniform float dens;uniform float spd;uniform float top;
    float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
    float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+1.),f.x),f.y);}
    float fbm(vec2 p){float v=0.,a=.5;for(int k=0;k<5;k++){v+=a*n(p);p=p*2.03+vec2(1.7,9.2);a*=.5;}return v;}
    void main(){vec2 uv=gl_FragCoord.xy/r;vec2 p=uv*vec2(r.x/r.y,1.)*2.2;
      float f=fbm(p+vec2(t*.05*spd,t*.012));f=fbm(p*1.3+vec2(f*1.6-t*.03*spd,f*.8));
      float fade=smoothstep(0.,top,1.-uv.y)*smoothstep(0.,.18,uv.y+.04);
      float a=clamp((f-.38)*dens,0.,1.)*fade;gl_FragColor=vec4(col*a,a);}`;
  function shader(cv, opts){
    const gl = cv.getContext('webgl', {premultipliedAlpha:true, alpha:true}); if (!gl) return null;
    const sh = (ty, src) => { const s = gl.createShader(ty); gl.shaderSource(s, src); gl.compileShader(s); return s; };
    const p = gl.createProgram();
    gl.attachShader(p, sh(gl.VERTEX_SHADER, 'attribute vec2 v;void main(){gl_Position=vec4(v,0,1);}'));
    gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FRAG)); gl.linkProgram(p); gl.useProgram(p);
    const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,1,-1,-1,1,1,1]), gl.STATIC_DRAW);
    const v = gl.getAttribLocation(p, 'v'); gl.enableVertexAttribArray(v); gl.vertexAttribPointer(v, 2, gl.FLOAT, false, 0, 0);
    const U = k => gl.getUniformLocation(p, k);
    gl.uniform2f(U('r'), cv.width, cv.height); gl.uniform3fv(U('col'), opts.col); gl.uniform1f(U('dens'), opts.dens);
    gl.uniform1f(U('spd'), opts.spd); gl.uniform1f(U('top'), opts.top);
    const ut = U('t'), ur = U('r');
    const size = () => { const w = 480, h = Math.max(8, Math.round(480 * cv.clientHeight / Math.max(1, cv.clientWidth)));
      cv.width = w; cv.height = h; gl.viewport(0, 0, w, h); gl.uniform2f(ur, w, h); };
    size(); onLayout.push(size);
    cv.dataset.webgl = 'true';
    const draw = t => { if (!gl.isContextLost()) { gl.uniform1f(ut, t + opts.off); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4); } };
    draw.destroy = () => { gl.deleteBuffer(b); gl.deleteProgram(p); gl.getExtension('WEBGL_lose_context')?.loseContext(); };
    return draw;
  }
  const fogBack  = shader(root.querySelector('.lv3hw-fog.lv3hw-back'),  {col:[.42,.62,.40], dens:1.35, spd:1.0, top:.85, off:0});
  const fogFront = shader(root.querySelector('.lv3hw-fog.lv3hw-front'), {col:[.50,.72,.46], dens:1.6,  spd:1.6, top:.95, off:40});
  const clouds   = shader(root.querySelector('.lv3hw-clouds'),    {col:[0,0,0],      dens:1.1,  spd:.6,  top:.0001, off:90});

  root.querySelector('.lv3hw-clouds').style.filter = 'invert(1)';


  const layers = [...root.querySelectorAll('[data-d]')].map(el => ({el, d:+el.dataset.d, f:el.dataset.f !== undefined ? +el.dataset.f : null, transform:null}));
  let mx = 960, my = 520, tx = 960, ty = 520, pointer = false, lastMove = 0, pendingPointer = null;
  let beamX = null, beamY = null;
  root.addEventListener('pointermove', e => {
    // The phone scene loops in place; pointer input must not steer its layers.
    if (mode === 'tall' || e.pointerType === 'touch') return;
    pendingPointer = { x: e.clientX, y: e.clientY };
  }, { signal, passive: true });
  root.addEventListener('pointerleave', () => { pointer = false; pendingPointer = null; }, { signal });
  root.addEventListener('pointercancel', () => { pointer = false; pendingPointer = null; }, { signal });


  const turb = root.querySelector('[data-lv3hw-turb]'), disp = root.querySelector('[data-lv3hw-disp]');
  const title = root.querySelector('.lv3hw-title');
  const glow = root.querySelector('.lv3hw-glow'), flash = root.querySelector('.lv3hw-flash'), moon = root.querySelector('.lv3hw-moon');
  const sheens = [...root.querySelectorAll('.lv3hw-sheen,.lv3hw-rimL')].map(el => ({ el, position: null }));

  const rimLs = [...root.querySelectorAll('.lv3hw-rimL')];
  const drips = [...root.querySelectorAll('.lv3hw-drip')].map(el => ({el, neck:el.querySelector('.lv3hw-neck'), drop:el.querySelector('.lv3hw-drop'), s:-99, idle:false}));


  let nextBolt = 3.5, bolt = null, swell = -99, sweep = -99, nextDrip = 9;
  function boltLevel(t){
    if (!bolt && t > nextBolt){ bolt = {s:t, double: Math.random() < .45}; swell = t + .25; sweep = t + .9; nextDrip = t + 3 + Math.random() * 2; }
    if (!bolt) return 0;
    const k = t - bolt.s; let L = 0;
    if (k < .07) L = 1; else if (k < .14) L = .25; else if (k < .2) L = bolt.double ? .85 : .1;
    else if (k < .9) L = Math.max(0, (bolt.double ? .6 : .3) * (1 - (k - .2) / .7));
    else { bolt = null; nextBolt = t + 10 + Math.random() * 6; }
    return L;
  }
  const bump = (t, s, up, down) => { const k = t - s; return k < 0 ? 0 : k < up ? k / up : Math.max(0, 1 - (k - up) / down); };

  let last = null, T = 0, lastFilter = 0, frameId = 0, running = false, lastFlash = null;
  function frame(now_){
    const dt = last === null ? 0 : Math.min(.05, (now_ - last) / 1000); last = now_; T += dt;
    const mobile = mode === 'tall';
    if (mobile) {
      // Anchor the phone composition without changing the ambient animation clock.
      pendingPointer = null; pointer = false;
      mx = tx = Lo.W / 2; my = ty = Lo.H * .48;
    } else {
      // Coalesce high-frequency pointer samples into one read before frame writes.
      if (pendingPointer) {
        const rect = root.getBoundingClientRect();
        tx = (pendingPointer.x - rect.left) / S; ty = (pendingPointer.y - rect.top) / S;
        pendingPointer = null; pointer = true; lastMove = T;
      }
      if (!pointer || T - lastMove > 4){
        tx = Lo.W/2 + (Math.sin(T * .23) * .29 + Math.sin(T * .51) * .08) * Lo.W;
        ty = Lo.H * .44 + Math.sin(T * .31 + 1) * Lo.H * .18;
      }
      mx += (tx - mx) * Math.min(1, dt * 4); my += (ty - my) * Math.min(1, dt * 4);
    }
    // Scope moving light variables to their owner rather than invalidating descendants.
    const bx = (mx*S).toFixed(1) + 'px', by = (my*S).toFixed(1) + 'px';
    if (beam && bx !== beamX) { beam.style.setProperty('--mx', bx); beamX = bx; }
    if (beam && by !== beamY) { beam.style.setProperty('--my', by); beamY = by; }
    const nx = (mx - Lo.W/2) / (Lo.W/2), ny = (my - Lo.H/2) / (Lo.H/2);
    layers.forEach(layer => {
      const {el, d, f} = layer;
      let x = mobile ? 0 : -nx * d, y = mobile ? 0 : -ny * d * .6, r = 0;
      if (f !== null){ y += Math.sin(T * .9 + f) * 6; r = Math.sin(T * .6 + f * 1.3) * .9; }
      const transform = mobile && f === null ? '' : `translate3d(${(x*S).toFixed(2)}px,${(y*S).toFixed(2)}px,0) rotate(${r.toFixed(2)}deg)`;
      if (transform !== layer.transform) { el.style.transform = transform; layer.transform = transform; }
    });

    const L = boltLevel(T);
    if (L !== lastFlash) {
      flash.style.opacity = (L * .85).toFixed(3);
      rimLs.forEach(r => r.style.opacity = (L * .9).toFixed(3));
      lastFlash = L;
    }
    const buzz = (Math.random() < .012 ? .6 : 1) * (.88 + Math.sin(T * 1.7) * .05);
    glow.style.opacity = (buzz * (mode === 'tall' ? .6 : 1)).toFixed(3);
    moon.style.opacity = (.7 + Math.sin(T * .27) * .2 + Math.sin(T * .71) * .1 + L * .5).toFixed(3);
    const sw = bump(T, swell, .35, 1.8);
    title.style.setProperty('--ta', (.3 + Math.sin(T * 1.1) * .05 + sw * .3).toFixed(3));
    title.style.setProperty('--tg', ((11 + Math.sin(T * 1.1) * 2 + sw * 12)*S).toFixed(1) + 'px');


    if (now_ - lastFilter > 40){
      lastFilter = now_;
      const bx = .006 + Math.sin(T * .3) * .001, by = .011 + Math.cos(T * .25) * .0015;
      turb.setAttribute('baseFrequency', `${(bx/S).toFixed(5)} ${(by/S).toFixed(5)}`);
      disp.setAttribute('scale', ((2.5 + Math.sin(T * .7) * 1.2 + sw * 3)*S).toFixed(2));
    }


    sheens.forEach((sheen, i) => {
      const k = (T - sweep - i * .22) / 1.1;
      const c = k < 0 || k > 1 ? -40 : -20 + k * 140;
      if (c === sheen.position) return;
      sheen.position = c;
      const s = sheen.el;
      s.style.setProperty('--a', (c - 12) + '%'); s.style.setProperty('--b', c + '%'); s.style.setProperty('--c', (c + 12) + '%');
    });


    if (T > nextDrip && !bolt){
      const free = drips.filter(d => T - d.s > 6);
      if (free.length){ free[Math.floor(Math.random() * free.length)].s = T; }
      nextDrip = T + 2.5 + Math.random() * 3.5;
    }
    drips.forEach(d => {
      const k = T - d.s;
      if (k < 0 || k > 3.2){
        if (!d.idle) { d.neck.setAttribute('height', '0'); d.drop.style.opacity = 0; d.idle = true; }
        return;
      }
      d.idle = false;

      const grow = k < 1.6 ? 1 - Math.pow(1 - k / 1.6, 3) : Math.max(0, 1 - (k - 1.6) / 1.6);
      d.neck.setAttribute('height', (6 + grow * 26).toFixed(1));
      if (k < 1.6){ d.drop.style.opacity = 1; d.drop.setAttribute('transform', `translate(0 ${(grow * 26 - 4).toFixed(1)}) scale(${(.6 + grow * .4).toFixed(2)})`); }
      else { const f = k - 1.6; d.drop.style.opacity = Math.max(0, 1 - f / .55).toFixed(2); d.drop.setAttribute('transform', `translate(0 ${(22 + 260 * f * f + 30 * f).toFixed(1)}) scale(${(1 - f * .3).toFixed(2)} ${(1 + f * .5).toFixed(2)})`); }
    });

    fogBack && fogBack(T); fogFront && fogFront(T); clouds && clouds(T);
    if (running) frameId = requestAnimationFrame(frame);
  }


  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
  observer?.observe(root);
  frame(performance.now());
  return {
    get elapsedTime() { return T; },
    setRunning(value) {
      if (running === value) return;
      running = value;
      if (running) { last = null; frameId = requestAnimationFrame(frame); }
      else { cancelAnimationFrame(frameId); last = null; }
    },
    destroy() {
      running = false;
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      for (const painter of [fogBack, fogFront, clouds]) painter?.destroy();
    },
  };
}

export class LV3HalloweenSale extends HTMLElement {
  connectedCallback() {
    if (this.connection) return;
    this.connection = new AbortController();
    this.start = parseSaleInstant(this.dataset.start);
    this.end = parseSaleInstant(this.dataset.end);
    this.preview = this.dataset.design === 'true' && Boolean(this.dataset.previewNow);
    this.enabled = this.dataset.enabled === 'true' || this.preview;
    this.label = this.querySelector('[data-clock-label]');
    this.countdown = this.querySelector('[data-countdown]');
    this.epoch = this.preview ? parseSaleInstant(this.dataset.previewNow) : Number(this.dataset.renderedAt) * 1000;
    this.anchor = performance.now();
    this.trusted = Number.isFinite(this.epoch) && this.epoch > 0;
    this.nextSync = 0;
    this.windowFocused = true;
    const signal = this.connection.signal;
    document.addEventListener('visibilitychange', () => {
      this.#resume();
    }, { signal });
    window.addEventListener('pageshow', event => {
      if (event.persisted && typeof document.hasFocus === 'function') this.windowFocused = document.hasFocus();
      this.#resume();
    }, { signal });
    window.addEventListener('focus', () => { this.windowFocused = true; this.#resume(); }, { signal });
    window.addEventListener('blur', () => { this.windowFocused = false; this.#motion(); }, { signal });
    this.#prepareMotion(signal);
    this.tick();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.connection?.abort();
    this.connection = null;
    this.pending = null;
    this.intersection?.disconnect();
    this.motionLayoutObserver?.disconnect();
    this.scene?.destroy();
    this.scene = null;
    this.hidden = true;
  }

  #resume() {
    this.tick();
    this.#motion();
    if (!document.hidden && !this.preview) void this.synchronize();
  }

  #prepareMotion(signal) {
    this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.reducedMotion?.addEventListener('change', () => this.#motion(), { signal });
    this.motionLayoutObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => this.#motion()) : null;
    this.motionLayoutObserver?.observe(this);
    this.inView = false;
    if (typeof IntersectionObserver === 'function') {
      this.intersection = new IntersectionObserver(entries => {
        this.inView = entries.some(entry => entry.isIntersecting);
        this.#motion();
      });
      this.intersection.observe(this);
    } else {
      this.inView = true;
    }
    const connection = this.connection;
    this.imagesReady = false;
    void Promise.all(Array.from(this.querySelectorAll('img'), image => {
      if (typeof image.decode === 'function') return image.decode().catch(() => {});
      if (image.complete) return Promise.resolve();
      return new Promise(resolve => {
        image.addEventListener('load', resolve, { once: true, signal });
        image.addEventListener('error', resolve, { once: true, signal });
      });
    })).then(() => {
      if (this.connection !== connection) return;
      this.imagesReady = true;
      this.#motion();
    });
  }

  #motion() {
    const width = this.clientWidth;
    const phone = width > 0 && width < 760;
    const paused = phone || this.hidden || document.hidden || !this.windowFocused || !this.inView || Boolean(this.reducedMotion?.matches) || this.dataset.motion !== 'true' || getComputedStyle(this).animationPlayState === 'paused';
    if (this.dataset.motionPaused !== String(paused)) this.dataset.motionPaused = String(paused);
    // A phone cold load stays static without creating WebGL contexts or a scene loop.
    if (this.imagesReady && !paused && !this.scene) {
      this.scene = createHalloweenMotion(this, this.connection.signal);
      this.dataset.motionReady = 'true';
    }
    this.scene?.setRunning(!paused);
  }

  async synchronize() {
    if (!this.connection || this.pending === this.connection) return;
    if (!this.enabled || !Number.isFinite(this.start) || !Number.isFinite(this.end) || this.start >= this.end) {
      this.update(NaN);
      return;
    }
    const connection = this.connection;
    this.pending = connection;
    try {
      if (!this.preview) {
        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('_lv3hw_clock', String(Math.round(performance.now())));
        const response = await fetch(url, {
          method: 'HEAD', cache: 'no-store', credentials: 'same-origin',
          signal: AbortSignal.any([connection.signal, AbortSignal.timeout(5000)]),
        });
        const epoch = Date.parse(response.headers.get('date') || '');
        const age = Number(response.headers.get('age') || 0);
        if (!response.ok || !Number.isFinite(epoch) || !Number.isFinite(age) || age !== 0) {
          throw new Error('A fresh storefront server time is unavailable');
        }
        if (this.connection !== connection) return;
        this.epoch = epoch;
        this.anchor = performance.now();
      }
      this.trusted = Number.isFinite(this.epoch) && this.epoch > 0;
      this.nextSync = performance.now() + 30000;
    } catch {
      if (this.connection !== connection) return;
      this.nextSync = performance.now() + 5000;
    } finally {
      if (this.connection === connection) {
        this.pending = null;
        this.tick();
      }
    }
  }

  update(now) {
    const state = saleState({ enabled: this.enabled, start: this.start, end: this.end, now });
    if (this.dataset.state !== state) this.dataset.state = state;
    if (this.hidden !== (state !== 'active')) this.hidden = state !== 'active';
    this.#motion();
    if (this.label) {
      const label = this.dataset[state === 'active' ? 'endsLabel' : 'inactiveLabel'];
      if (this.label.textContent !== label) this.label.textContent = label;
    }
    if (this.countdown) {
      let seconds = Math.max(0, Math.ceil((this.end - now) / 1000));
      const days = Math.floor(seconds / 86400);
      seconds %= 86400;
      const hours = Math.floor(seconds / 3600);
      seconds %= 3600;
      const minutes = Math.floor(seconds / 60);
      seconds %= 60;
      const countdown = state === 'active'
        ? `${days ? days + ' ' + this.dataset.daysLabel + ' · ' : ''}${[hours, minutes, seconds].map(n => String(n).padStart(2, '0')).join(':')}`
        : '';
      if (this.countdown.textContent !== countdown) this.countdown.textContent = countdown;
    }
  }

  tick() {
    clearTimeout(this.timer);
    if (!this.connection) return;
    const elapsed = performance.now() - this.anchor;
    this.update(this.trusted ? this.epoch + elapsed : NaN);
    if (!this.enabled || !Number.isFinite(this.start) || !Number.isFinite(this.end) || this.start >= this.end) return;
    if (document.hidden) return;
    if (!this.preview && this.enabled && performance.now() >= this.nextSync) void this.synchronize();
    this.timer = setTimeout(() => this.tick(), 250);
  }
}

if (!customElements.get('lv3-halloween-sale')) {
  customElements.define('lv3-halloween-sale', LV3HalloweenSale);
}
